/**
 * KPI Dashboard Widget (Story 10-9: Operational KPI Dashboard).
 * Displays real-time KPIs: orders (today/week/month), revenue (this month),
 * customers (total/new/repeat), operations (pending/in_delivery/success_rate/avg_time).
 * Admin only.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getKPIDashboard, type KPIDashboardResponse } from "../../services/reportsService";
import { getOrders } from "../../services/orderService";
import { formatCurrency } from "../../utils/currency";
import { formatDecimal, formatPercentDisplay } from "../../utils/numberFormat";
import { getApiErrorMessage } from "../../utils/apiError";

// Route constants for navigation
const ROUTES = {
  ORDERS: "/orders",
  REPORTS: "/reports",
} as const;

function TrendIndicator({
  value,
  isPercent = false,
}: {
  value: number | null;
  isPercent?: boolean;
}) {
  if (value === null || value === 0) {
    return <span className="text-neutral-500 dark:text-neutral-400">—</span>;
  }
  const isPositive = value > 0;
  const colorClass = isPositive
    ? "text-green-600 dark:text-green-400"
    : "text-red-600 dark:text-red-400";
  const icon = isPositive ? "↑" : "↓";
  const displayValue = isPercent
    ? value > 0
      ? `+${formatPercentDisplay(value)}`
      : formatPercentDisplay(Math.abs(value))
    : formatDecimal(Math.abs(value));

  return (
    <span className={colorClass}>
      {icon} {displayValue}
    </span>
  );
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number | null;
  isPercent?: boolean;
  onClick?: () => void;
  className?: string;
}

function KPICard({
  title,
  value,
  subtitle,
  trend,
  isPercent,
  onClick,
  className = "",
}: KPICardProps) {
  const cardClasses = onClick
    ? "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors"
    : "";
  const content = (
    <div className={`p-3 ${cardClasses} ${className}`}>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-0.5">{title}</p>
      <div className="flex items-baseline gap-2 flex-wrap">
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {value}
        </p>
        {trend !== undefined && (
          <span className="text-xs">
            <TrendIndicator value={trend} isPercent={isPercent} />
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  );

  if (onClick) {
    return (
      <div
        onClick={onClick}
        className="rounded-lg border border-neutral-200 dark:border-neutral-700"
      >
        {content}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">{content}</div>
  );
}

export function KPIDashboardWidget() {
  const { t } = useTranslation("dashboard");
  const [kpi, setKpi] = useState<KPIDashboardResponse | null>(null);
  const [livePendingValidation, setLivePendingValidation] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getKPIDashboard(),
      getOrders({
        status: ["to_validate"],
        page: 1,
        limit: 1,
      }),
    ])
      .then(([kpiData, pendingOrders]) => {
        if (cancelled) return;
        setKpi(kpiData);
        setLivePendingValidation(pendingOrders.total);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("kpi_error_loading"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("kpi_loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!kpi) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_data")}</p>
      </div>
    );
  }

  const pendingValidation = livePendingValidation ?? kpi.operations.pending_validation;

  return (
    <div className="space-y-4">
      {/* Orders Section */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          {t("kpi_section_orders")}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link to={ROUTES.ORDERS} className="block">
            <KPICard
              title={t("kpi_today")}
              value={formatDecimal(kpi.orders.today.count)}
              subtitle={
                kpi.orders.today.change !== null && kpi.orders.today.change !== 0
                  ? `${formatCurrency(kpi.orders.today.value)} (${kpi.orders.today.change >= 0 ? "↑" : "↓"} ${formatDecimal(Math.abs(kpi.orders.today.change))} ${t("kpi_from_yesterday")})`
                  : formatCurrency(kpi.orders.today.value)
              }
              trend={kpi.orders.today.change}
            />
          </Link>
          <Link to={ROUTES.ORDERS} className="block">
            <KPICard
              title={t("kpi_this_week")}
              value={formatDecimal(kpi.orders.this_week.count)}
              subtitle={
                kpi.orders.this_week.change_pct !== null && kpi.orders.this_week.change_pct !== 0
                  ? `${formatCurrency(kpi.orders.this_week.value)} (${kpi.orders.this_week.change_pct >= 0 ? "↑" : "↓"} ${formatPercentDisplay(Math.abs(kpi.orders.this_week.change_pct))} ${t("kpi_from_last_week")})`
                  : formatCurrency(kpi.orders.this_week.value)
              }
              trend={kpi.orders.this_week.change_pct}
              isPercent
            />
          </Link>
          <Link to={ROUTES.ORDERS} className="block">
            <KPICard
              title={t("kpi_this_month")}
              value={formatDecimal(kpi.orders.this_month.count)}
              subtitle={
                kpi.orders.this_month.change_pct !== null && kpi.orders.this_month.change_pct !== 0
                  ? `${formatCurrency(kpi.orders.this_month.value)} (${kpi.orders.this_month.change_pct >= 0 ? "↑" : "↓"} ${formatPercentDisplay(Math.abs(kpi.orders.this_month.change_pct))} ${t("kpi_from_last_month")})`
                  : formatCurrency(kpi.orders.this_month.value)
              }
              trend={kpi.orders.this_month.change_pct}
              isPercent
            />
          </Link>
        </div>
      </div>

      {/* Revenue Section */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          {t("kpi_section_revenue")}
        </h4>
        <Link to={ROUTES.REPORTS} className="block">
          <KPICard
            title={t("kpi_this_month")}
            value={formatCurrency(kpi.revenue.this_month)}
            subtitle={
              kpi.revenue.change_pct !== null && kpi.revenue.change_pct !== 0
                ? `(${kpi.revenue.change_pct >= 0 ? "↑" : "↓"} ${formatPercentDisplay(Math.abs(kpi.revenue.change_pct))} ${t("kpi_from_last_month")})`
                : undefined
            }
            trend={kpi.revenue.change_pct}
            isPercent
          />
        </Link>
      </div>

      {/* Customers Section */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          {t("kpi_section_customers")}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link to={ROUTES.REPORTS} className="block">
            <KPICard title={t("kpi_total_customers")} value={formatDecimal(kpi.customers.total)} />
          </Link>
          <Link to={ROUTES.REPORTS} className="block">
            <KPICard title={t("kpi_new_this_month")} value={formatDecimal(kpi.customers.new_this_month)} />
          </Link>
          <Link to={ROUTES.REPORTS} className="block">
            <KPICard title={t("kpi_repeat_this_month")} value={formatDecimal(kpi.customers.repeat_this_month)} />
          </Link>
        </div>
      </div>

      {/* Operations Section */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          {t("kpi_section_operations")}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to={ROUTES.ORDERS} className="block">
            <KPICard
              title={t("kpi_pending_validation")}
              value={formatDecimal(pendingValidation)}
            />
          </Link>
          <Link to={ROUTES.ORDERS} className="block">
            <KPICard title={t("kpi_in_delivery")} value={formatDecimal(kpi.operations.in_delivery)} />
          </Link>
          <Link to={ROUTES.REPORTS} className="block">
            <KPICard
              title={t("kpi_delivery_success_rate")}
              value={
                kpi.operations.delivery_success_rate !== null
                  ? formatPercentDisplay(kpi.operations.delivery_success_rate, {
                      maximumFractionDigits: 1,
                    })
                  : "—"
              }
            />
          </Link>
          <Link to={ROUTES.REPORTS} className="block">
            <KPICard
              title={t("kpi_avg_delivery_time")}
              value={
                kpi.operations.avg_delivery_hours !== null
                  ? `${formatDecimal(kpi.operations.avg_delivery_hours, {
                      maximumFractionDigits: 1,
                    })}h`
                  : "—"
              }
            />
          </Link>
        </div>
      </div>
    </div>
  );
}
